import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle,
  Globe,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Save,
  Trash2,
  User,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import {
  formatAffiliateDate,
  affiliateStatusColors as statusColors,
  affiliateStatusLabels as statusLabels,
} from './model';
import type { Affiliate, AffiliateAgent, AffiliateOwnerOption } from './model';

type AffiliateEditData = Partial<
  Affiliate & { agent_id?: string | null; owner_user_id?: string | null }
>;
type AsyncAction = () => void | Promise<void>;

export interface AffiliateDialogsController {
  selectedAffiliate: Affiliate | null;
  showDetailModal: boolean;
  showDeleteConfirm: boolean;
  showApproveModal: boolean;
  showRejectModal: boolean;
  showCreateGymModal: boolean;
  isEditMode: boolean;
  editData: AffiliateEditData;
  agentsList: AffiliateAgent[];
  ownerOptions: AffiliateOwnerOption[];
  isSaving: boolean;
  isProcessing: boolean;
  isDeleting: boolean;
  isLoadingAgents: boolean;
  deleteConfirmName: string;
  approveAgentId: string;
  showCreateAgent: boolean;
  newAgentEmail: string;
  newAgentName: string;
  newAgentTerritory: string;
  isCreatingAgent: boolean;
  rejectReason: string;
  newGymName: string;
  newGymAddress: string;
  newGymPhone: string;
  newGymEmail: string;
  newGymAgentId: string;
  isCreatingGym: boolean;
  setShowDetailModal: Dispatch<SetStateAction<boolean>>;
  setShowDeleteConfirm: Dispatch<SetStateAction<boolean>>;
  setShowApproveModal: Dispatch<SetStateAction<boolean>>;
  setShowRejectModal: Dispatch<SetStateAction<boolean>>;
  setShowCreateGymModal: Dispatch<SetStateAction<boolean>>;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  setEditData: Dispatch<SetStateAction<AffiliateEditData>>;
  setDeleteConfirmName: Dispatch<SetStateAction<string>>;
  setApproveAgentId: Dispatch<SetStateAction<string>>;
  setShowCreateAgent: Dispatch<SetStateAction<boolean>>;
  setNewAgentEmail: Dispatch<SetStateAction<string>>;
  setNewAgentName: Dispatch<SetStateAction<string>>;
  setNewAgentTerritory: Dispatch<SetStateAction<string>>;
  setRejectReason: Dispatch<SetStateAction<string>>;
  setNewGymName: Dispatch<SetStateAction<string>>;
  setNewGymAddress: Dispatch<SetStateAction<string>>;
  setNewGymPhone: Dispatch<SetStateAction<string>>;
  setNewGymEmail: Dispatch<SetStateAction<string>>;
  setNewGymAgentId: Dispatch<SetStateAction<string>>;
  handleEditClick: AsyncAction;
  handleSaveEdit: AsyncAction;
  handleDeleteAffiliate: AsyncAction;
  handleApprove: AsyncAction;
  handleCreateAgent: AsyncAction;
  handleReject: AsyncAction;
  handleSuspend: AsyncAction;
  handleActivate: AsyncAction;
  handleCreateGym: AsyncAction;
}

export function AffiliateDialogs({
  controller,
}: {
  controller: AffiliateDialogsController;
}) {
  const {
    selectedAffiliate,
    showDetailModal,
    showDeleteConfirm,
    showApproveModal,
    showRejectModal,
    showCreateGymModal,
    isEditMode,
    editData,
    agentsList,
    ownerOptions,
    isSaving,
    isProcessing,
    isDeleting,
    isLoadingAgents,
    deleteConfirmName,
    approveAgentId,
    showCreateAgent,
    newAgentEmail,
    newAgentName,
    newAgentTerritory,
    isCreatingAgent,
    rejectReason,
    newGymName,
    newGymAddress,
    newGymPhone,
    newGymEmail,
    newGymAgentId,
    isCreatingGym,
    setShowDetailModal,
    setShowDeleteConfirm,
    setShowApproveModal,
    setShowRejectModal,
    setShowCreateGymModal,
    setIsEditMode,
    setEditData,
    setDeleteConfirmName,
    setApproveAgentId,
    setShowCreateAgent,
    setNewAgentEmail,
    setNewAgentName,
    setNewAgentTerritory,
    setRejectReason,
    setNewGymName,
    setNewGymAddress,
    setNewGymPhone,
    setNewGymEmail,
    setNewGymAgentId,
    handleEditClick,
    handleSaveEdit,
    handleDeleteAffiliate,
    handleApprove,
    handleCreateAgent,
    handleReject,
    handleSuspend,
    handleActivate,
    handleCreateGym,
  } = controller;

  return (
    <>
      {/* Detail Modal */}
      {selectedAffiliate && showDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>{isEditMode ? 'Edit Affiliate' : selectedAffiliate.name}</CardTitle>
                  <CardDescription>
                    {isEditMode ? selectedAffiliate.name : 'Affiliate Details'}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  {!isEditMode && selectedAffiliate.source !== 'application' && (
                    <Button variant="ghost" size="sm" onClick={handleEditClick}>
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowDetailModal(false);
                      setIsEditMode(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={
                    statusColors[selectedAffiliate.subscription_status] ||
                    'bg-gray-500/10 text-gray-400'
                  }
                >
                  {statusLabels[selectedAffiliate.subscription_status] ||
                    selectedAffiliate.subscription_status}
                </Badge>
              </div>

              {/* Info Grid — Edit or Read-only */}
              {isEditMode ? (
                <div className="grid gap-3">
                  <div>
                    <Label htmlFor="edit-name">Gym Name</Label>
                    <Input
                      id="edit-name"
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Contact Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editData.contact_email || ''}
                      onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Phone</Label>
                    <Input
                      id="edit-phone"
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-whatsapp">WhatsApp</Label>
                    <Input
                      id="edit-whatsapp"
                      value={editData.whatsapp || ''}
                      onChange={(e) => setEditData({ ...editData, whatsapp: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-address">Address</Label>
                    <Input
                      id="edit-address"
                      value={editData.address || ''}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-country">Country Code</Label>
                    <Input
                      id="edit-country"
                      value={editData.country_code || ''}
                      onChange={(e) => setEditData({ ...editData, country_code: e.target.value })}
                      placeholder="e.g. GB, US, AU"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-owner">Gym Owner</Label>
                    <select
                      id="edit-owner"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editData.owner_user_id || ''}
                      onChange={(e) =>
                        setEditData({ ...editData, owner_user_id: e.target.value || null })
                      }
                    >
                      <option value="">Unassigned</option>
                      {ownerOptions.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name} ({owner.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="edit-agent">Assigned Agent</Label>
                    <select
                      id="edit-agent"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editData.agent_id || ''}
                      onChange={(e) =>
                        setEditData({ ...editData, agent_id: e.target.value || null })
                      }
                    >
                      <option value="">No agent assigned</option>
                      {agentsList.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name} {agent.territory_name ? `(${agent.territory_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                    <Users className="h-4 w-4" /> {selectedAffiliate.member_count} members
                  </div>
                </div>
              ) : (
                <div className="grid gap-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="rounded-full bg-primary/10 p-2">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Owner</p>
                      <p className="text-sm font-medium">
                        {selectedAffiliate.owner?.name || 'Unassigned'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedAffiliate.owner?.email || '—'}
                      </p>
                    </div>
                  </div>

                  {selectedAffiliate.contact_email && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-primary/10 p-2">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Contact Email</p>
                        <p className="text-sm font-medium">{selectedAffiliate.contact_email}</p>
                      </div>
                    </div>
                  )}

                  {selectedAffiliate.phone && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-primary/10 p-2">
                        <Phone className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-sm font-medium">{selectedAffiliate.phone}</p>
                      </div>
                    </div>
                  )}

                  {selectedAffiliate.whatsapp && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-primary/10 p-2">
                        <MessageCircle className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">WhatsApp</p>
                        <p className="text-sm font-medium">{selectedAffiliate.whatsapp}</p>
                      </div>
                    </div>
                  )}

                  {(selectedAffiliate.address || selectedAffiliate.city) && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-primary/10 p-2">
                        <MapPin className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sm font-medium">
                          {[selectedAffiliate.address, selectedAffiliate.city]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedAffiliate.country_code && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-primary/10 p-2">
                        <Globe className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Country</p>
                        <p className="text-sm font-medium">{selectedAffiliate.country_code}</p>
                      </div>
                    </div>
                  )}

                  {selectedAffiliate.agent && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="rounded-full bg-blue-500/10 p-2">
                        <User className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Referring Agent</p>
                        <p className="text-sm font-medium">{selectedAffiliate.agent.name}</p>
                        {selectedAffiliate.agent.territory_name && (
                          <p className="text-xs text-muted-foreground">
                            {selectedAffiliate.agent.territory_name}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Members</p>
                      <p className="text-sm font-medium">{selectedAffiliate.member_count}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Applied</p>
                      <p className="text-sm font-medium">
                        {formatAffiliateDate(selectedAffiliate.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              {isEditMode ? (
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={handleSaveEdit} disabled={isSaving}>
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setIsEditMode(false)}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </div>
                  {selectedAffiliate.source !== 'application' && (
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => {
                        setDeleteConfirmName('');
                        setShowDeleteConfirm(true);
                      }}
                      disabled={isSaving}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Gym
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex gap-2 pt-4 border-t border-border">
                  {selectedAffiliate.subscription_status === 'pending' && (
                    <>
                      <Button
                        className="flex-1"
                        onClick={() => {
                          setShowDetailModal(false);
                          setShowApproveModal(true);
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 text-destructive hover:text-destructive"
                        onClick={() => {
                          setShowDetailModal(false);
                          setShowRejectModal(true);
                        }}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  {selectedAffiliate.subscription_status === 'active' && (
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={handleSuspend}
                      disabled={isProcessing}
                    >
                      {isProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Suspend Affiliate
                    </Button>
                  )}
                  {selectedAffiliate.subscription_status === 'cancelled' && (
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={handleActivate}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Activate Affiliate
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                    Close
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {selectedAffiliate && showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Delete Gym
                  </CardTitle>
                  <CardDescription>{selectedAffiliate.name}</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowDeleteConfirm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  This action cannot be undone.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  All data will be permanently deleted: {selectedAffiliate.member_count} member(s),
                  scores, events, and programming overrides.
                </p>
              </div>
              <div>
                <Label>
                  Type <strong>{selectedAffiliate.name}</strong> to confirm
                </Label>
                <Input
                  className="mt-2"
                  placeholder="Type gym name..."
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDeleteAffiliate}
                  disabled={isDeleting || deleteConfirmName !== selectedAffiliate.name}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete Permanently
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {selectedAffiliate && showApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Approve Affiliate</CardTitle>
                  <CardDescription>{selectedAffiliate.name}</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowApproveModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to approve <strong>{selectedAffiliate.name}</strong>?
              </p>
              <p className="text-sm text-muted-foreground">This will:</p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>Activate their subscription</li>
                <li>Send a welcome email to {selectedAffiliate.owner?.email || 'the owner'}</li>
                {approveAgentId && <li>Credit the referring agent</li>}
              </ul>

              {/* Agent Assignment */}
              <div className="space-y-2">
                <Label htmlFor="approve-agent">Assign Agent (optional)</Label>
                {isLoadingAgents ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading agents...
                  </div>
                ) : (
                  <select
                    id="approve-agent"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={approveAgentId}
                    onChange={(e) => setApproveAgentId(e.target.value)}
                  >
                    <option value="">No agent</option>
                    {agentsList.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                        {agent.territory_name ? ` (${agent.territory_name})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {!showCreateAgent && !isLoadingAgents && (
                  <Button
                    variant="link"
                    className="h-auto p-0 text-xs"
                    onClick={() => setShowCreateAgent(true)}
                  >
                    + Create new agent
                  </Button>
                )}
              </div>

              {/* Inline Create Agent */}
              {showCreateAgent && (
                <div className="space-y-3 rounded-md border border-border p-3">
                  <p className="text-sm font-medium">Create New Agent</p>
                  <div className="space-y-2">
                    <Input
                      placeholder="Agent email *"
                      value={newAgentEmail}
                      onChange={(e) => setNewAgentEmail(e.target.value)}
                    />
                    <Input
                      placeholder="Agent name (optional)"
                      value={newAgentName}
                      onChange={(e) => setNewAgentName(e.target.value)}
                    />
                    <Input
                      placeholder="Territory (optional)"
                      value={newAgentTerritory}
                      onChange={(e) => setNewAgentTerritory(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateAgent} disabled={isCreatingAgent}>
                      {isCreatingAgent ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                      Create
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowCreateAgent(false);
                        setNewAgentEmail('');
                        setNewAgentName('');
                        setNewAgentTerritory('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button className="flex-1" onClick={handleApprove} disabled={isProcessing}>
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
                <Button variant="outline" onClick={() => setShowApproveModal(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reject Modal */}
      {selectedAffiliate && showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-destructive">Reject Application</CardTitle>
                  <CardDescription>{selectedAffiliate.name}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reject-reason">Rejection Reason (optional)</Label>
                <textarea
                  id="reject-reason"
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Provide a reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                The applicant will be notified via email with this reason.
              </p>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleReject}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Reject Application
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Gym Modal */}
      {showCreateGymModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Add Gym
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateGymModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Gyms can be physical places, clubs, or associations where people gather to train.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gym-name">Gym Name *</Label>
                <Input
                  id="gym-name"
                  placeholder="e.g. CrossFit Hackney"
                  value={newGymName}
                  onChange={(e) => setNewGymName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gym-address">Address *</Label>
                <Input
                  id="gym-address"
                  placeholder="e.g. 123 High Street, London"
                  value={newGymAddress}
                  onChange={(e) => setNewGymAddress(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gym-phone">Phone *</Label>
                <Input
                  id="gym-phone"
                  placeholder="e.g. +44 20 1234 5678"
                  value={newGymPhone}
                  onChange={(e) => setNewGymPhone(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gym-email">Contact Email *</Label>
                <Input
                  id="gym-email"
                  type="email"
                  placeholder="e.g. owner@crossfithackney.com"
                  value={newGymEmail}
                  onChange={(e) => setNewGymEmail(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Approval and welcome emails are sent to this address.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gym-agent">Assigned Agent</Label>
                <Select
                  options={[
                    { value: '', label: 'ErgShoot HQ (default)' },
                    ...agentsList.map((a) => ({
                      value: a.id,
                      label: a.territory_name ? `${a.name} (${a.territory_name})` : a.name,
                    })),
                  ]}
                  value={newGymAgentId}
                  onChange={(e) => setNewGymAgentId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  If no agent is selected, defaults to ErgShoot HQ.
                </p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCreateGymModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateGym}
                  disabled={isCreatingGym || !newGymName.trim()}
                >
                  {isCreatingGym ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Building2 className="mr-2 h-4 w-4" />
                      Create Gym
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
